import { type AnimationDefinition, motion, type Variants } from 'motion/react';
import { useEffect, useState } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';

interface LoaderProps {
  status?: 'loading' | 'complete';
  autoCompleteIn?: number;
  onComplete?(): void;
}

const containerLoadingVariants: Variants = {
  initial: { opacity: 1, scale: 0.8 },
  animate: {
    opacity: [0.5, 0.8, 0.5],
    scale: [0.5, 0.8, 0.5],
    transition: {
      repeatDelay: 0.05,
      repeat: Infinity,
      ease: 'easeInOut',
      duration: 2,
    },
  },
};

const containerCompleteVariants: Variants = {
  initial: { opacity: 0.8, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      ease: 'easeIn',
      duration: 0.5,
    },
  },
};

const contentLoadingVariants = {
  initial: { opacity: 0.0 },
  animate: { opacity: 0.0 },
};

const contentCompleteVariants = {
  initial: { opacity: 0.0, scale: 1 },
  animate: {
    opacity: 1.0,
    scale: 0.85,
    transition: {
      duration: 0.5,
    },
  },
};

export function Loader({
  autoCompleteIn,
  status = 'loading',
  onComplete,
}: LoaderProps) {
  const [isComplete, setIsComplete] = useState<boolean>(status === 'complete');

  useEffect(() => {
    if (!autoCompleteIn) return;

    const timeout = setTimeout(() => setIsComplete(true), autoCompleteIn);

    return () => clearTimeout(timeout);
  }, [autoCompleteIn]);

  useEffect(() => {
    setIsComplete(status === 'complete');
  }, [status]);

  const getStatus = () => {
    if (isComplete) return 'complete';

    return status;
  };

  const getContainerVariant = () => {
    switch (getStatus()) {
      case 'complete':
        return containerCompleteVariants;
      default:
        return containerLoadingVariants;
    }
  };

  const getContentVariant = () => {
    switch (getStatus()) {
      case 'complete':
        return contentCompleteVariants;
      default:
        return contentLoadingVariants;
    }
  };

  const handleOnAnimationComplete = (animation: AnimationDefinition) => {
    if (animation === 'animate' && getStatus() === 'complete' && onComplete) {
      onComplete();
    }
  };

  return (
    <div
      className="flex items-center justify-center size-full"
      role="status"
      aria-label={getStatus() === 'complete' ? 'Loading complete' : 'Loading'}
      aria-live="polite"
    >
      <motion.div
        variants={getContainerVariant()}
        initial="initial"
        animate="animate"
        className="bg-foreground rounded-full size-full flex"
      >
        <motion.div
          variants={getContentVariant()}
          onAnimationComplete={handleOnAnimationComplete}
          className="m-auto size-full flex items-center justify-center"
        >
          <AppLogo size="75%" invert />
        </motion.div>
      </motion.div>
    </div>
  );
}
